import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Mp3Encoder } from '@breezystack/lamejs';
import {
  FaAddressBook,
  FaArrowLeft,
  FaBan,
  FaCheck,
  FaCheckCircle,
  FaCheckDouble,
  FaClock,
  FaDownload,
  FaEllipsisV,
  FaEnvelope,
  FaExclamationTriangle,
  FaEyeSlash,
  FaFileAlt,
  FaImage,
  FaInfoCircle,
  FaKeyboard,
  FaMicrophone,
  FaPaperPlane,
  FaPlus,
  FaRobot,
  FaReply,
  FaSearch,
  FaSmile,
  FaSpinner,
  FaStickyNote,
  FaStop,
  FaSyncAlt,
  FaTimes,
  FaTrash,
  FaVideo,
} from 'react-icons/fa';
import { useColumns } from '../../contexts/columnsContext';
import { useUser } from '../../contexts/userContext';
import { useWhatsappZapi } from '../../contexts/whatsappZapiContext';
import { preferredWhatsappInstanceId } from '../../contexts/whatsappChannelMerge';
import api from '../../services/api';
import PreviewCard from '../PreviewCard';
import QuickRepliesPanel from './QuickRepliesPanel';
import StickersPanel from './StickersPanel';
import { uploadSticker } from './stickers';
import { fillQuickReply, filterQuickReplies, getSlashQuery } from './quickReplies';
import {
  formatServiceWindowRemaining,
  getMessageIdentityPresentation,
  getMessageStatusPresentation,
} from './whatsappMessageStatus';
import {
  getMetaMediaBlob,
  getMetaMediaBrowserCacheStatus,
} from './whatsappMediaBrowserCache';

const META_MEDIA_RULES = {
  image: { max: 5 * 1024 * 1024, mime: new Set(['image/jpeg', 'image/png']), label: 'imagem' },
  audio: { max: 16 * 1024 * 1024, mime: new Set(['audio/aac', 'audio/mp4', 'audio/mpeg', 'audio/amr', 'audio/ogg']), label: 'áudio' },
  video: { max: 16 * 1024 * 1024, mime: new Set(['video/mp4', 'video/3gpp']), label: 'vídeo' },
  document: {
    max: 100 * 1024 * 1024,
    mime: new Set([
      'text/plain',
      'application/pdf',
      'application/msword',
      'application/vnd.ms-excel',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ]),
    label: 'documento',
  },
};

const META_IMAGE_ACCEPT = 'image/jpeg,image/png';
const META_VIDEO_ACCEPT = 'video/mp4,video/3gpp';
const META_DOCUMENT_ACCEPT = [
  'text/plain',
  'application/pdf',
  'application/msword',
  'application/vnd.ms-excel',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
].join(',');

const MESSAGE_TYPE_LABELS = {
  image: 'Imagem',
  audio: 'Áudio',
  video: 'Vídeo',
  document: 'Documento',
  sticker: 'Figurinha',
  template: 'Modelo de mensagem',
};

const QUICK_EMOJIS = [
  '😀', '😊', '😂', '😍', '👍', '🙏', '👏', '🎉',
  '❤️', '✅', '🤔', '😢', '😮', '🔥', '💙', '📌',
];

function isSupportedMetaTemplate(template) {
  const format = String(template?.parameter_format || 'POSITIONAL').toUpperCase();
  if (format !== 'POSITIONAL') return false;
  const components = Array.isArray(template?.components) ? template.components : [];
  const allowed = new Set(['HEADER', 'BODY', 'FOOTER', 'BUTTONS']);
  if (components.some((component) => !allowed.has(String(component?.type || '').toUpperCase()))) return false;
  const body = components.find((component) => String(component?.type || '').toUpperCase() === 'BODY');
  const bodyTokens = String(body?.text || '').match(/\{\{([^}]+)\}\}/g) || [];
  if (bodyTokens.some((token) => !/^\{\{\d+\}\}$/.test(token))) return false;
  return components
    .filter((component) => String(component?.type || '').toUpperCase() !== 'BODY')
    .every((component) => !/\{\{[^}]+\}\}/.test(JSON.stringify(component)));
}

function getContactId(contact) {
  return contact?.contact_id || contact?.id;
}

function formatDateTime(value) {
  if (!value) return '';
  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function isExpired(value) {
  if (!value) return false;
  return new Date(value).getTime() < Date.now();
}

function messageContentPreview(message) {
  const text = String(message?.text_body || message?.caption || '').trim();
  if (text) return text;
  return MESSAGE_TYPE_LABELS[String(message?.message_type || '').toLowerCase()] || 'Mensagem';
}

function formatConversationPreview(value) {
  const preview = String(value || '').trim();
  const match = preview.match(/^\[([^\]]+)\]$/);
  if (!match) return preview || 'Sem mensagens';
  return MESSAGE_TYPE_LABELS[match[1].toLowerCase()] || preview;
}

function mediaFileName(message, mimeType) {
  const requested = String(message?.media_file_name || '').trim();
  if (requested) return requested.replace(/[\\/:*?"<>|]+/g, '_');
  const type = String(message?.message_type || 'arquivo').toLowerCase();
  const extensions = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'audio/aac': 'aac',
    'audio/mp4': 'm4a',
    'audio/mpeg': 'mp3',
    'audio/amr': 'amr',
    'audio/ogg': 'ogg',
    'video/mp4': 'mp4',
    'video/3gpp': '3gp',
    'application/pdf': 'pdf',
  };
  const extension = extensions[String(mimeType || message?.media_mime_type || '').toLowerCase()] || '';
  return `whatsapp-${type}-${message?.id || Date.now()}${extension ? `.${extension}` : ''}`;
}

async function downloadMessageMedia(message, empresaId, userId) {
  let blob;
  if (message.provider === 'meta') {
    blob = await getMetaMediaBlob(message.id, empresaId, userId);
  } else {
    const response = await fetch(message.media_url, { credentials: 'omit' });
    if (!response.ok) throw new Error('Não foi possível baixar esta mídia.');
    blob = await response.blob();
  }

  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = mediaFileName(message, blob.type);
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }
}

function MessageReplyReference({ message, contact }) {
  if (!message.context_message_id) return null;
  const original = {
    message_type: message.replied_message_type,
    text_body: message.replied_message_text,
    caption: message.replied_message_caption,
  };
  const author = message.replied_message_direction === 'outbound'
    ? 'Syncs CRM'
    : contact?.name || contact?.phone_display || contact?.phone_normalized || 'Cliente';

  return (
    <div className="zapi-whatsapp-replied-message">
      <strong>{author}</strong>
      <span>{message.replied_message_local_id ? messageContentPreview(original) : 'Mensagem respondida'}</span>
    </div>
  );
}

function formatBytes(value) {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function storagePresentation(kind, status, enabled = true) {
  if (kind === 'browser') {
    if (status === 'stored') return { tone: 'success', title: 'Disponível neste navegador', detail: 'Abertura rápida enquanto o cache estiver preservado.' };
    if (status === 'missing') return { tone: 'neutral', title: 'Ainda não está no cache', detail: 'Será salvo neste navegador quando a mídia for carregada.' };
    return { tone: 'warning', title: 'Cache indisponível', detail: 'Este navegador não permitiu consultar o cache local.' };
  }
  if (kind === 'cloud' && !enabled && status !== 'stored') {
    return { tone: 'neutral', title: 'Não habilitado', detail: 'A réplica no Supabase está desativada para esta empresa.' };
  }
  if (status === 'stored') {
    return {
      tone: 'success',
      title: kind === 'cloud' && !enabled ? 'Cópia existente' : 'Armazenado',
      detail: kind === 'cloud' && !enabled
        ? 'Esta cópia permanece salva; novas réplicas estão desativadas.'
        : 'A gravação foi confirmada.',
    };
  }
  if (status === 'pending' || status === 'not_registered') {
    return { tone: 'warning', title: 'Aguardando confirmação', detail: 'O sistema tentará confirmar o armazenamento automaticamente.' };
  }
  if (status === 'failed') {
    return { tone: 'danger', title: 'Falha detectada', detail: 'Uma nova tentativa automática está programada.' };
  }
  return { tone: 'warning', title: 'Status indisponível', detail: 'Não foi possível consultar este armazenamento.' };
}

function StorageStatusIcon({ tone }) {
  if (tone === 'success') return <FaCheckCircle aria-hidden="true" />;
  if (tone === 'danger') return <FaExclamationTriangle aria-hidden="true" />;
  if (tone === 'warning') return <FaClock aria-hidden="true" />;
  return <FaInfoCircle aria-hidden="true" />;
}

function MediaStorageStatusModal({ message, empresaId, userId, theme, onClose }) {
  const [browser, setBrowser] = useState({ status: 'loading' });
  const [storage, setStorage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const refresh = async () => {
    setLoading(true);
    setError('');
    const [browserResult, storageResult] = await Promise.allSettled([
      getMetaMediaBrowserCacheStatus(message.id, empresaId, userId),
      api.get(`/whatsapp-meta/messages/${message.id}/media-storage-status`),
    ]);
    if (browserResult.status === 'fulfilled') {
      const value = browserResult.value;
      setBrowser({
        ...value,
        status: !value.supported ? 'unavailable' : value.cached ? 'stored' : 'missing',
      });
    } else {
      setBrowser({ supported: false, cached: false, status: 'unavailable' });
    }
    if (storageResult.status === 'fulfilled') {
      setStorage(storageResult.value.data);
    } else {
      setStorage(null);
      setError(storageResult.reason?.response?.data?.message || 'Não foi possível consultar o servidor agora.');
    }
    setLoading(false);
  };

  useEffect(() => {
    void refresh();
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // A consulta deve ocorrer somente na abertura deste diagnóstico.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message.id, empresaId, userId]);

  const browserView = storagePresentation('browser', browser.status);
  const primaryView = storagePresentation('primary', storage?.primary?.status || 'unavailable');
  const cloudView = storagePresentation(
    'cloud',
    storage?.cloud?.status || 'unavailable',
    Boolean(storage?.cloud?.enabled),
  );
  const verifiedAt = storage?.primary?.last_verified_at || storage?.primary?.stored_at;
  const portalTarget = document.querySelector('[data-zapi-whatsapp]') || document.body;

  return createPortal(
    <div className="zapi-whatsapp-storage-modal-backdrop" data-zapi-whatsapp data-theme={theme === 'dark' ? 'dark' : 'light'} onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="zapi-whatsapp-storage-modal" role="dialog" aria-modal="true" aria-labelledby={`media-storage-title-${message.id}`}>
        <div className="zapi-whatsapp-storage-modal-header">
          <div>
            <h3 id={`media-storage-title-${message.id}`}>Armazenamento da mídia</h3>
            <span>Mensagem #{message.id}</span>
          </div>
          <button type="button" className="zapi-whatsapp-icon-button" onClick={onClose} title="Fechar" aria-label="Fechar">
            <FaTimes />
          </button>
        </div>
        <div className="zapi-whatsapp-storage-modal-body">
          {loading && <div className="zapi-whatsapp-storage-loading"><FaSpinner /> Consultando os armazenamentos...</div>}
          {!loading && error && <div className="zapi-whatsapp-storage-alert"><FaExclamationTriangle /> {error}</div>}
          {!loading && (
            <div className="zapi-whatsapp-storage-list">
              <div className={`zapi-whatsapp-storage-item zapi-whatsapp-storage-item--${browserView.tone}`}>
                <StorageStatusIcon tone={browserView.tone} />
                <div><strong>Cache deste navegador</strong><b>{browserView.title}</b><span>{browserView.detail}</span>{browser.cached_at && <small>Salvo em {formatDateTime(browser.cached_at)}{browser.size_bytes ? ` · ${formatBytes(browser.size_bytes)}` : ''}</small>}</div>
              </div>
              <div className={`zapi-whatsapp-storage-item zapi-whatsapp-storage-item--${primaryView.tone}`}>
                <StorageStatusIcon tone={primaryView.tone} />
                <div><strong>Servidor local</strong><b>{primaryView.title}</b><span>{primaryView.detail}</span>{verifiedAt && <small>Confirmado em {formatDateTime(verifiedAt)}</small>}</div>
              </div>
              <div className={`zapi-whatsapp-storage-item zapi-whatsapp-storage-item--${cloudView.tone}`}>
                <StorageStatusIcon tone={cloudView.tone} />
                <div><strong>Supabase</strong><b>{cloudView.title}</b><span>{cloudView.detail}</span>{storage?.cloud?.stored_at && <small>Salvo em {formatDateTime(storage.cloud.stored_at)}</small>}</div>
              </div>
            </div>
          )}
          {!loading && storage?.next_retry_at && (storage?.primary?.status === 'failed' || storage?.cloud?.status === 'failed') && (
            <p className="zapi-whatsapp-storage-retry">Próxima tentativa: {formatDateTime(storage.next_retry_at)}.</p>
          )}
          <p className="zapi-whatsapp-storage-note">O cache do navegador é temporário. O servidor local é a cópia principal; o Supabase é uma réplica adicional quando habilitado.</p>
        </div>
        <div className="zapi-whatsapp-storage-modal-footer">
          <button type="button" onClick={() => void refresh()} disabled={loading}><FaSyncAlt /> Atualizar status</button>
        </div>
      </div>
    </div>,
    portalTarget,
  );
}

function MessageActions({ message, onReply, onError, empresaId, userId, theme }) {
  const [open, setOpen] = useState(false);
  const [storageModalOpen, setStorageModalOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [savingSticker, setSavingSticker] = useState(false);
  const rootRef = useRef(null);
  const canReply = message.provider === 'meta' && Boolean(message.provider_message_id || message.meta_message_id);
  const canSaveSticker = message.provider === 'meta'
    && String(message.message_type || '').toLowerCase() === 'sticker'
    && Boolean(message.media_id);
  const canDownload = Boolean(
    (message.media_id || message.media_url)
    && !['text', 'template'].includes(String(message.message_type || '').toLowerCase()),
  );
  const canInspectStorage = message.provider === 'meta' && Boolean(message.media_id) && canDownload;

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  if (!canReply && !canDownload && !canInspectStorage) return null;

  const handleSaveSticker = async () => {
    setSavingSticker(true);
    try {
      const blob = await getMetaMediaBlob(message.id, empresaId, userId);
      await uploadSticker(blob);
      setOpen(false);
      onError('Figurinha salva na sua biblioteca.');
    } catch (error) {
      onError(error.response?.data?.message || error.message || 'Não foi possível salvar a figurinha.');
    } finally {
      setSavingSticker(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadMessageMedia(message, empresaId, userId);
      setOpen(false);
    } catch (error) {
      onError(error.response?.data?.message || error.message || 'Não foi possível baixar esta mídia.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="zapi-whatsapp-message-actions" ref={rootRef}>
      {canInspectStorage && (
        <button
          type="button"
          className="zapi-whatsapp-message-menu-trigger"
          onClick={() => { setOpen(false); setStorageModalOpen(true); }}
          title="Ver armazenamento da mídia"
          aria-label="Ver armazenamento da mídia"
        >
          <FaInfoCircle />
        </button>
      )}
      <button
        type="button"
        className="zapi-whatsapp-message-menu-trigger"
        onClick={() => setOpen((current) => !current)}
        title="Ações da mensagem"
        aria-label="Ações da mensagem"
        aria-expanded={open}
      >
        <FaEllipsisV />
      </button>
      {open && (
        <div className="zapi-whatsapp-message-action-menu" role="menu">
          {canReply && (
            <button type="button" role="menuitem" onClick={() => { onReply(message); setOpen(false); }}>
              <FaReply /> Responder
            </button>
          )}
          {canDownload && (
            <button type="button" role="menuitem" onClick={handleDownload} disabled={downloading}>
              <FaDownload /> {downloading ? 'Baixando...' : 'Baixar mídia'}
            </button>
          )}
          {canSaveSticker && (
            <button type="button" role="menuitem" onClick={handleSaveSticker} disabled={savingSticker}>
              <FaStickyNote /> {savingSticker ? 'Salvando...' : 'Salvar figurinha'}
            </button>
          )}
        </div>
      )}
      {storageModalOpen && (
        <MediaStorageStatusModal
          message={message}
          empresaId={empresaId}
          userId={userId}
          theme={theme}
          onClose={() => setStorageModalOpen(false)}
        />
      )}
    </div>
  );
}

function MessageDeliveryStatus({ message }) {
  const status = getMessageStatusPresentation(message);
  const statusIcon = status.icon === 'double-check'
    ? <FaCheckDouble />
    : status.icon === 'check'
      ? <FaCheck />
      : status.icon === 'error'
        ? <FaExclamationTriangle />
        : status.icon === 'clock'
          ? <FaClock />
          : null;

  if (String(message.direction || '').toLowerCase() !== 'outbound') {
    return null;
  }

  return (
    <span
      className={`zapi-whatsapp-delivery-status zapi-whatsapp-delivery-status--${status.tone}`}
      title={status.label}
      aria-label={status.label}
    >
      {statusIcon}
    </span>
  );
}

async function convertRecordedAudioToMp3(blob) {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) throw new Error('Conversão de áudio indisponível neste navegador.');

  const audioContext = new AudioContextClass();
  try {
    const audioBuffer = await audioContext.decodeAudioData(await blob.arrayBuffer());
    const samples = new Int16Array(audioBuffer.length);
    const channelCount = audioBuffer.numberOfChannels;

    for (let index = 0; index < audioBuffer.length; index += 1) {
      let sample = 0;
      for (let channel = 0; channel < channelCount; channel += 1) {
        sample += audioBuffer.getChannelData(channel)[index];
      }
      sample = Math.max(-1, Math.min(1, sample / channelCount));
      samples[index] = sample < 0 ? sample * 32768 : sample * 32767;
    }

    const encoder = new Mp3Encoder(1, audioBuffer.sampleRate, 128);
    const mp3Chunks = [];
    const blockSize = 1152;
    for (let offset = 0; offset < samples.length; offset += blockSize) {
      const encoded = encoder.encodeBuffer(samples.subarray(offset, offset + blockSize));
      if (encoded.length) mp3Chunks.push(encoded);
    }
    const finalChunk = encoder.flush();
    if (finalChunk.length) mp3Chunks.push(finalChunk);
    return new Blob(mp3Chunks, { type: 'audio/mpeg' });
  } finally {
    await audioContext.close();
  }
}

function MessageMedia({ message, empresaId, userId }) {
  if (message.provider === 'meta') {
    return <MetaMessageMedia message={message} empresaId={empresaId} userId={userId} />;
  }
  if (!message.media_url && message.message_type !== 'text') {
    return (
      <div className="zapi-whatsapp-media-expired">
        Mídia registrada sem arquivo no CRM. {message.media_file_name ? `Arquivo: ${message.media_file_name}. ` : ''}
        Veja a mídia diretamente no WhatsApp/celular.
      </div>
    );
  }
  if (!message.media_url) return null;
  if (message.media_expires_at && isExpired(message.media_expires_at)) {
    return (
      <div className="zapi-whatsapp-media-expired">
        Mídia indisponível. Arquivos recebidos podem expirar no provedor e este arquivo não está mais disponível para download.
      </div>
    );
  }

  if (message.message_type === 'image' || message.message_type === 'sticker') {
    return <img className="zapi-whatsapp-message-image" src={message.media_url} alt={message.caption || 'Imagem'} />;
  }

  if (message.message_type === 'audio') {
    return <audio className="zapi-whatsapp-message-audio" controls src={message.media_url} />;
  }

  if (message.message_type === 'video') {
    return <video className="zapi-whatsapp-message-video" controls src={message.media_url} />;
  }

  return (
    <a className="zapi-whatsapp-document-link" href={message.media_url} target="_blank" rel="noreferrer">
      <FaFileAlt /> {message.media_file_name || message.caption || 'Abrir documento'}
    </a>
  );
}

function MetaMessageMedia({ message, empresaId, userId }) {
  const [mediaUrl, setMediaUrl] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    let objectUrl = '';
    if (!message.media_id) return undefined;
    getMetaMediaBlob(message.id, empresaId, userId)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setMediaUrl(objectUrl);
      })
      .catch(() => active && setError('Mídia oficial indisponível. Consulte o WhatsApp se ela já tiver expirado na Meta.'));
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [message.id, message.media_id, empresaId, userId]);

  if (!message.media_id) {
    if (message.message_type === 'text' || message.message_type === 'template') return null;
    return <div className="zapi-whatsapp-media-expired">Conteúdo {message.message_type} registrado sem arquivo.</div>;
  }
  if (error) return <div className="zapi-whatsapp-media-expired">{error}</div>;
  if (!mediaUrl) {
    const mediaLabel = (MESSAGE_TYPE_LABELS[message.message_type] || 'Mídia').toLowerCase();
    return (
      <div
        className={`zapi-whatsapp-media-loading zapi-whatsapp-media-loading--${message.message_type || 'media'}`}
        role="status"
        aria-live="polite"
      >
        <FaSpinner aria-hidden="true" />
        <span>Carregando {mediaLabel}...</span>
      </div>
    );
  }
  if (message.message_type === 'image' || message.message_type === 'sticker') return <img className="zapi-whatsapp-message-image" src={mediaUrl} alt={message.caption || (message.message_type === 'sticker' ? 'Figurinha' : 'Imagem')} />;
  if (message.message_type === 'audio') return <audio className="zapi-whatsapp-message-audio" controls src={mediaUrl} />;
  if (message.message_type === 'video') return <video className="zapi-whatsapp-message-video" controls src={mediaUrl} />;
  return <a className="zapi-whatsapp-document-link" href={mediaUrl} download={message.media_file_name || 'arquivo'}><FaFileAlt /> {message.media_file_name || 'Abrir arquivo'}</a>;
}

function WhatsappZapiModal({ theme = 'light' }) {
  const {
    closeWhatsappZapi,
    conversations,
    loadConversations,
    selectedContact,
    selectConversation,
    markConversationRead,
    markConversationUnread,
    messages,
    loadMessages,
    availableInstances,
    loadAvailableInstances,
    linkedCards,
    loadingConversations,
    loadingMessages,
    lastError,
    setLastError,
    sendMessage,
    sendFileMessage,
    loadMetaTemplates,
    recordMetaOptIn,
    loadMetaOptInStatus,
    metaMarketingEnabled,
    suggestAiReply,
    updateContact,
    archiveContact,
    createCardFromContact,
  } = useWhatsappZapi();
  const { columns } = useColumns();
  const { user, listAllUsers } = useUser();
  const messagesContainerRef = useRef(null);
  const userIsReadingHistoryRef = useRef(false);
  const previousContactIdRef = useRef('');
  const previousLastMessageKeyRef = useRef('');
  const forceScrollToBottomRef = useRef(false);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const messageInputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordingStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const cancelRecordingRef = useRef(false);
  const sendIntentRef = useRef(null);
  const channelChoiceRef = useRef({ contactId: '', manual: false });

  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('');
  const [messageType, setMessageType] = useState('text');
  const [text, setText] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [fileName, setFileName] = useState('');
  const [selectedMediaFile, setSelectedMediaFile] = useState(null);
  const [selectedInstanceId, setSelectedInstanceId] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingAiSuggestion, setLoadingAiSuggestion] = useState(false);
  const [archivingContact, setArchivingContact] = useState(false);
  const [contactForm, setContactForm] = useState({});
  const [cardForm, setCardForm] = useState({ column_id: '', entity_id: '' });
  const [linkedCardPreviews, setLinkedCardPreviews] = useState([]);
  const [loadingLinkedCardPreviews, setLoadingLinkedCardPreviews] = useState(false);
  const [sideModal, setSideModal] = useState('');
  const [recordingAudio, setRecordingAudio] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [metaTemplates, setMetaTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateValues, setTemplateValues] = useState([]);
  const [optInSource, setOptInSource] = useState('');
  const [optInEvidence, setOptInEvidence] = useState('');
  // null = ainda verificando; objeto = resposta do servidor; 'error' = falha na consulta.
  const [optInStatus, setOptInStatus] = useState(null);
  const [unsupportedTemplateCount, setUnsupportedTemplateCount] = useState(0);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [replyingTo, setReplyingTo] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [mobilePane, setMobilePane] = useState(selectedContact ? 'chat' : 'conversations');

  const contactId = getContactId(selectedContact);
  const isLoadError = /^Erro ao carregar (conversas|mensagens)/.test(lastError || '');
  const selectedConversationHasUnread = conversations.some(
    (conversation) => String(getContactId(conversation)) === String(contactId)
      && Number(conversation.unread_count || 0) > 0,
  );

  const markSelectedConversationRead = () => {
    if (contactId && selectedConversationHasUnread) void markConversationRead(contactId);
  };

  const [markingUnread, setMarkingUnread] = useState(false);
  const handleMarkUnread = async () => {
    if (!contactId || markingUnread) return;
    setMarkingUnread(true);
    try {
      await markConversationUnread(contactId);
      setMobilePane('conversations');
      setLastError('');
    } catch (error) {
      setLastError(error.response?.data?.message || 'Não foi possível marcar a conversa como não lida.');
    } finally {
      setMarkingUnread(false);
    }
  };

  useEffect(() => {
    loadAvailableInstances();
  }, [loadAvailableInstances]);

  useEffect(() => {
    if (availableInstances.length === 0) {
      if (selectedInstanceId) setSelectedInstanceId('');
      return;
    }
    const selectedStillAllowed = availableInstances.some((instance) => String(instance.id) === String(selectedInstanceId));
    if (!selectedStillAllowed) {
      const needsChannelChoice = availableInstances.length > 1
        && availableInstances.some((instance) => instance.provider === 'meta');
      setSelectedInstanceId(needsChannelChoice ? '' : String(availableInstances[0].id));
    }
  }, [availableInstances, selectedInstanceId]);

  useEffect(() => {
    const nextContactId = String(contactId || '');
    if (channelChoiceRef.current.contactId === nextContactId) return;
    channelChoiceRef.current = { contactId: nextContactId, manual: false };
    if (availableInstances.some((instance) => instance.provider === 'meta')) {
      setSelectedInstanceId('');
    }
  }, [contactId, availableInstances]);

  useEffect(() => {
    if (!contactId || channelChoiceRef.current.manual) return;
    if (!availableInstances.some((instance) => instance.provider === 'meta')) return;
    const preferred = preferredWhatsappInstanceId(messages, availableInstances, contactId);
    if (preferred && preferred !== selectedInstanceId) setSelectedInstanceId(preferred);
  }, [contactId, messages, availableInstances, selectedInstanceId]);

  useEffect(() => {
    if (selectedContact) {
      setContactForm({
        name: selectedContact.name || '',
        phone: selectedContact.phone_display || selectedContact.phone_normalized || '',
        email: selectedContact.email || '',
        country: selectedContact.country || 'Brasil',
        state: selectedContact.state || '',
        city: selectedContact.city || '',
        notes: selectedContact.notes || '',
      });
    }
  }, [selectedContact]);

  useEffect(() => {
    setSideModal('');
    setReplyingTo(null);
    setShowEmojiPicker(false);
    setMobilePane(contactId ? 'chat' : 'conversations');
  }, [contactId]);

  useEffect(() => {
    if (!showEmojiPicker) return undefined;
    const close = (event) => {
      if (!emojiPickerRef.current?.contains(event.target)) setShowEmojiPicker(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [showEmojiPicker]);

  useEffect(() => {
    if (replyingTo && String(replyingTo.instance_id) !== String(selectedInstanceId)) {
      setReplyingTo(null);
    }
  }, [replyingTo, selectedInstanceId]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || !contactId) return;

    const normalizedContactId = String(contactId);
    const contactChanged = previousContactIdRef.current !== normalizedContactId;
    const lastMessage = messages[messages.length - 1];
    const lastMessageKey = lastMessage
      ? `${lastMessage.provider || 'zapi'}:${lastMessage.id || lastMessage.meta_message_id || lastMessage.message_id || ''}`
      : '';
    const lastMessageChanged = previousLastMessageKeyRef.current !== lastMessageKey;

    previousContactIdRef.current = normalizedContactId;
    previousLastMessageKeyRef.current = lastMessageKey;

    const shouldScrollToBottom = contactChanged
      || forceScrollToBottomRef.current
      || (lastMessageChanged && !userIsReadingHistoryRef.current);
    if (!shouldScrollToBottom) return;

    const expectedContactId = normalizedContactId;
    window.requestAnimationFrame(() => {
      if (previousContactIdRef.current !== expectedContactId) return;
      const activeContainer = messagesContainerRef.current;
      if (!activeContainer) return;
      activeContainer.scrollTop = activeContainer.scrollHeight;
      userIsReadingHistoryRef.current = false;
      forceScrollToBottomRef.current = false;
    });
  }, [contactId, messages]);

  const handleConversationScroll = (event) => {
    const container = event.currentTarget;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    userIsReadingHistoryRef.current = distanceFromBottom > 120;
    markSelectedConversationRead();
  };

  useEffect(() => {
    let cancelled = false;

    const loadLinkedCardPreviews = async () => {
      if (!linkedCards.length) {
        setLinkedCardPreviews([]);
        setLoadingLinkedCardPreviews(false);
        return;
      }

      setLoadingLinkedCardPreviews(true);
      try {
        const cards = await Promise.all(
          linkedCards.map(async (card) => {
            try {
              const response = await api.get(`/card/find-by-id/${card.card_id}`);
              return {
                ...card,
                ...response.data,
                column_name: card.column_name || response.data?.column_name,
                owner_username: card.owner_username || response.data?.owner_username,
              };
            } catch (error) {
              return card;
            }
          }),
        );

        if (!cancelled) setLinkedCardPreviews(cards);
      } finally {
        if (!cancelled) setLoadingLinkedCardPreviews(false);
      }
    };

    loadLinkedCardPreviews();

    return () => {
      cancelled = true;
    };
  }, [linkedCards]);

  const selectedInstance = useMemo(
    () => availableInstances.find((instance) => String(instance.id) === String(selectedInstanceId)),
    [availableInstances, selectedInstanceId],
  );
  const preferredInstanceId = useMemo(
    () => preferredWhatsappInstanceId(messages, availableInstances, contactId),
    [messages, availableInstances, contactId],
  );
  const selectedTemplate = useMemo(
    () => metaTemplates.find((template) => String(template.id) === String(selectedTemplateId)),
    [metaTemplates, selectedTemplateId],
  );
  const selectedInstanceIsMeta = selectedInstance?.provider === 'meta' || String(selectedInstanceId).startsWith('meta:');
  const selectedMetaServiceWindowExpiresAt = useMemo(() => {
    if (!selectedInstanceIsMeta) return null;
    return messages
      .filter((message) => message.provider === 'meta' && String(message.instance_id) === String(selectedInstanceId))
      .map((message) => message.service_window_expires_at)
      .filter(Boolean)
      .sort((left, right) => new Date(right).getTime() - new Date(left).getTime())[0] || null;
  }, [messages, selectedInstanceId, selectedInstanceIsMeta]);
  const metaServiceWindowOpen = Boolean(
    selectedInstanceIsMeta
    && selectedMetaServiceWindowExpiresAt
    && new Date(selectedMetaServiceWindowExpiresAt).getTime() > currentTime,
  );
  const metaFreeFormBlocked = selectedInstanceIsMeta && !metaServiceWindowOpen;
  const selectedTemplatePurpose = selectedTemplate?.category === 'MARKETING'
    ? 'marketing'
    : selectedTemplate?.category === 'AUTHENTICATION' ? 'authentication' : 'transactional';
  const selectedTemplateCategoryLabel = selectedTemplate?.category === 'MARKETING'
    ? 'Marketing'
    : selectedTemplate?.category === 'AUTHENTICATION' ? 'Autenticação' : 'Utilidade';
  const selectedTemplateMarketingBlocked = selectedTemplate?.category === 'MARKETING' && !metaMarketingEnabled;
  const selectedTemplateConsentGranted = Boolean(
    optInStatus && optInStatus !== 'error' && optInStatus[selectedTemplatePurpose],
  );
  const optInStatusChannelId = selectedInstance?.meta_channel_id || selectedInstanceId;
  // Mesma ordem das travas do servidor, para o atendente ver o motivo antes de tentar enviar.
  const templateBlockReason = messageType !== 'template'
    ? ''
    : !selectedTemplate
      ? 'Selecione um modelo aprovado.'
      : selectedTemplateMarketingBlocked
        ? 'Modelos de Marketing estão desligados pelo administrador. Use um modelo de Utilidade.'
        : optInStatus === null
          ? 'Verificando consentimento do cliente...'
          : optInStatus === 'error'
            ? 'Não foi possível verificar o consentimento. Tente novamente.'
            : !selectedTemplateConsentGranted
              ? `Passo 1 pendente: registre o consentimento de ${selectedTemplateCategoryLabel} antes de enviar.`
              : templateValues.some((value) => !value.trim())
                ? 'Preencha todos os campos do modelo.'
                : '';
  const serviceWindowRemaining = metaServiceWindowOpen
    ? formatServiceWindowRemaining(selectedMetaServiceWindowExpiresAt, currentTime)
    : '';

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(Date.now()), 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!selectedInstanceIsMeta) {
      setMetaTemplates([]);
      setSelectedTemplateId('');
      setUnsupportedTemplateCount(0);
      return undefined;
    }
    loadMetaTemplates(selectedInstanceId)
      .then((items) => {
        if (cancelled) return;
        const approved = (items || []).filter((item) => item.status === 'APPROVED');
        const supported = approved.filter(isSupportedMetaTemplate);
        setMetaTemplates(supported);
        setUnsupportedTemplateCount(approved.length - supported.length);
      })
      .catch(() => !cancelled && setMetaTemplates([]));
    return () => { cancelled = true; };
  }, [loadMetaTemplates, selectedInstanceId, selectedInstanceIsMeta]);

  const refreshOptInStatus = useCallback(async () => {
    setOptInStatus(null);
    try {
      const status = await loadMetaOptInStatus(contactId, optInStatusChannelId);
      setOptInStatus(status || 'error');
    } catch (error) {
      setOptInStatus('error');
    }
  }, [contactId, loadMetaOptInStatus, optInStatusChannelId]);

  useEffect(() => {
    if (messageType !== 'template' || !selectedInstanceIsMeta || !contactId || !optInStatusChannelId) {
      setOptInStatus(null);
      return;
    }
    refreshOptInStatus();
  }, [contactId, messageType, optInStatusChannelId, refreshOptInStatus, selectedInstanceIsMeta]);

  useEffect(() => {
    if (!selectedTemplate) {
      setTemplateValues([]);
      return;
    }
    const body = (selectedTemplate.components || []).find((component) => component.type === 'BODY');
    const matches = String(body?.text || '').match(/\{\{(\d+)\}\}/g) || [];
    const count = matches.reduce((max, token) => Math.max(max, Number(token.replace(/\D/g, '')) || 0), 0);
    setTemplateValues(Array.from({ length: count }, () => ''));
  }, [selectedTemplate]);

  useEffect(() => {
    if (!recordingAudio) return undefined;
    const interval = setInterval(() => setRecordingSeconds((current) => current + 1), 1000);
    return () => clearInterval(interval);
  }, [recordingAudio]);

  useEffect(() => () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      cancelRecordingRef.current = true;
      mediaRecorderRef.current.stop();
    }
    recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
  }, []);

  const runSearch = async () => {
    await loadConversations({ term: searchTerm, filter });
  };

  const retryLoading = async () => {
    setLastError('');
    await Promise.all([
      loadConversations({ term: searchTerm, filter }),
      contactId ? loadMessages(contactId) : Promise.resolve([]),
    ]);
  };

  const resetMediaDraft = () => {
    setMediaUrl('');
    setCaption('');
    setFileName('');
    setSelectedMediaFile(null);
  };

  const formatRecordingDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const rest = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
  };

  const clientRequestIdFor = (signature) => {
    if (sendIntentRef.current?.signature === signature) return sendIntentRef.current.id;
    const id = window.crypto?.randomUUID?.()
      || `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
    sendIntentRef.current = { signature, id };
    return id;
  };

  const handleSend = async (event) => {
    event.preventDefault();
    if (!selectedInstanceId || !selectedInstance || !contactId || sending) return;
    if (recordingAudio) return;
    if (messageType === 'text' && !text.trim()) return;
    if (messageType === 'template' && templateBlockReason) {
      setLastError(templateBlockReason);
      return;
    }
    if (metaFreeFormBlocked && messageType !== 'template') {
      setLastError('A janela de 24 horas está fechada. Para proteger o número, use somente um modelo aprovado com consentimento registrado.');
      return;
    }
    if (!['text', 'template'].includes(messageType) && !mediaUrl.trim() && !selectedMediaFile) return;

    if (selectedInstanceIsMeta && (messageType === 'template'
      || (preferredInstanceId && preferredInstanceId !== selectedInstanceId))) {
      const category = selectedTemplate?.category === 'MARKETING'
        ? 'Marketing'
        : selectedTemplate?.category === 'AUTHENTICATION' ? 'Autenticação' : 'Utilidade';
      const differentNumber = preferredInstanceId && preferredInstanceId !== selectedInstanceId
        ? 'A última mensagem recebida nesta conversa veio de outro canal. Confira o número selecionado.\n\n'
        : '';
      const templateNotice = messageType === 'template'
        ? `Modelo: ${selectedTemplate.name} (${category}). Confirme que a finalidade está correta, que o cliente autorizou este tipo de contato e que você aceita a possível cobrança da Meta.\n\n`
        : '';
      if (!window.confirm(`${differentNumber}${templateNotice}Enviar para ${selectedContact?.phone_display || selectedContact?.phone_normalized || 'este contato'} usando ${selectedInstance.phone_display || selectedInstance.name}?`)) return;
    }

    forceScrollToBottomRef.current = true;
    userIsReadingHistoryRef.current = false;
    setSending(true);
    try {
      const signature = JSON.stringify({
        instance: selectedInstanceId,
        contact: contactId,
        type: messageType,
        text: text.trim(),
        mediaUrl: mediaUrl.trim(),
        caption: caption.trim(),
        fileName: fileName.trim(),
        file: selectedMediaFile ? {
          name: selectedMediaFile.name,
          size: selectedMediaFile.size,
          type: selectedMediaFile.type,
          lastModified: selectedMediaFile.lastModified,
        } : null,
        template: selectedTemplate?.id || null,
        values: templateValues,
        replyToMessageId: replyingTo?.id || null,
      });
      const clientRequestId = clientRequestIdFor(signature);
      if (messageType === 'template') {
        const parameters = templateValues.map((value) => ({ type: 'text', text: value }));
        await sendMessage({
          instanceId: selectedInstanceId,
          type: 'template',
          templateId: selectedTemplate.id,
          components: parameters.length ? [{ type: 'body', parameters }] : [],
          clientRequestId,
          replyToMessageId: replyingTo?.id,
        });
      } else if (selectedMediaFile) {
        await sendFileMessage({
          instanceId: selectedInstanceId,
          type: messageType,
          file: selectedMediaFile,
          caption: caption.trim() || text.trim(),
          fileName: fileName.trim() || selectedMediaFile.name,
          clientRequestId,
          replyToMessageId: replyingTo?.id,
        });
      } else {
        await sendMessage({
          instanceId: selectedInstanceId,
          type: messageType,
          text: text.trim(),
          mediaUrl: mediaUrl.trim(),
          caption: caption.trim(),
          fileName: fileName.trim(),
          extension: (fileName.split('.').pop() || 'pdf').toLowerCase(),
          clientRequestId,
          replyToMessageId: replyingTo?.id,
        });
      }
      setText('');
      resetMediaDraft();
      setMessageType('text');
      setSelectedTemplateId('');
      setReplyingTo(null);
      setShowEmojiPicker(false);
      sendIntentRef.current = null;
      setLastError('');
    } catch (error) {
      forceScrollToBottomRef.current = false;
      setLastError(error.response?.data?.message || error.message || 'Não foi possível enviar a mensagem.');
    } finally {
      setSending(false);
    }
  };

  const recordTemplateConsent = async (status, purposeOverride) => {
    if (
      !contactId
      || (!selectedTemplate && purposeOverride !== 'all')
      || !optInSource.trim()
      || !optInEvidence.trim()
    ) return;
    try {
      await recordMetaOptIn(contactId, {
        channel_id: selectedInstance.meta_channel_id,
        purpose: purposeOverride || (selectedTemplate.category === 'MARKETING' ? 'marketing' : selectedTemplate.category === 'AUTHENTICATION' ? 'authentication' : 'transactional'),
        status,
        source: optInSource.trim(),
        evidence: optInEvidence.trim(),
      });
      setOptInSource('');
      setOptInEvidence('');
      refreshOptInStatus();
      setLastError(status === 'granted'
        ? 'Consentimento registrado. Agora você pode enviar o modelo.'
        : purposeOverride === 'all'
          ? 'Pedido de interrupção geral registrado. O CRM bloqueará novos envios até uma nova autorização válida.'
          : 'Cancelamento do consentimento registrado. Não envie este modelo.');
      if (status === 'revoked' && purposeOverride === 'all') setSideModal('');
    } catch (error) {
      setLastError(error.response?.data?.message || 'Não foi possível registrar o consentimento.');
    }
  };

  // Mensagens rapidas: lista carregada ao abrir o chat; falha nao afeta o envio normal.
  const [quickReplies, setQuickReplies] = useState([]);
  const [quickRepliesError, setQuickRepliesError] = useState('');
  const [slashIndex, setSlashIndex] = useState(0);
  const [slashDismissed, setSlashDismissed] = useState(false);

  const loadQuickReplies = useCallback(async () => {
    try {
      const response = await api.get('/mensagens-rapidas');
      setQuickReplies(Array.isArray(response.data) ? response.data : []);
      setQuickRepliesError('');
    } catch (error) {
      setQuickReplies([]);
      setQuickRepliesError(error.response?.data?.message || 'Mensagens rápidas indisponíveis no momento.');
    }
  }, []);

  useEffect(() => { loadQuickReplies(); }, [loadQuickReplies]);

  const quickRepliesEnabled = messageType === 'text' && !metaFreeFormBlocked && !recordingAudio;
  const slashQuery = quickRepliesEnabled ? getSlashQuery(text) : null;
  const slashSuggestions = slashQuery === null ? [] : filterQuickReplies(quickReplies, slashQuery);
  const showSlashSuggestions = slashSuggestions.length > 0 && !slashDismissed;

  useEffect(() => {
    setSlashIndex(0);
    setSlashDismissed(false);
  }, [slashQuery]);

  // Figurinha sai na hora, como no WhatsApp; erros voltam para o painel de figurinhas.
  const handleSendSticker = async (blob) => {
    if (!selectedInstanceId || !contactId) throw new Error('Selecione uma conversa.');
    // Mesma trava do envio de texto: nao responder por um numero diferente sem confirmar.
    if (preferredInstanceId && preferredInstanceId !== selectedInstanceId
      && !window.confirm(`A última mensagem recebida nesta conversa veio de outro canal. Enviar a figurinha usando ${selectedInstance?.phone_display || selectedInstance?.name || 'o número selecionado'}?`)) {
      return;
    }
    setSending(true);
    try {
      forceScrollToBottomRef.current = true;
      userIsReadingHistoryRef.current = false;
      await sendFileMessage({
        instanceId: selectedInstanceId,
        type: 'sticker',
        file: new File([blob], 'figurinha.webp', { type: 'image/webp' }),
        caption: '',
        fileName: 'figurinha.webp',
        clientRequestId: window.crypto?.randomUUID?.()
          || `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`,
        replyToMessageId: replyingTo?.id,
      });
      setReplyingTo(null);
    } catch (error) {
      forceScrollToBottomRef.current = false;
      throw error;
    } finally {
      setSending(false);
    }
  };

  const applyQuickReply = (reply) => {
    const filled = fillQuickReply(reply.texto, selectedContact);
    const input = messageInputRef.current;
    // Vindo do "/atalho", substitui o atalho; vindo do botao, insere onde esta o cursor.
    const replaceAll = getSlashQuery(text) !== null || !text;
    const start = replaceAll ? 0 : (input?.selectionStart ?? text.length);
    const end = replaceAll ? text.length : (input?.selectionEnd ?? text.length);
    const nextText = `${text.slice(0, start)}${filled}${text.slice(end)}`;
    setText(nextText);
    setShowEmojiPicker(false);
    window.setTimeout(() => {
      input?.focus();
      input?.setSelectionRange(start + filled.length, start + filled.length);
    }, 0);
  };

  const handleMessageKeyDown = (event) => {
    if (showSlashSuggestions && !event.nativeEvent?.isComposing) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        setSlashIndex((current) => (current + step + slashSuggestions.length) % slashSuggestions.length);
        return;
      }
      if ((event.key === 'Enter' && !event.shiftKey) || event.key === 'Tab') {
        event.preventDefault();
        applyQuickReply(slashSuggestions[Math.min(slashIndex, slashSuggestions.length - 1)]);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setSlashDismissed(true);
        return;
      }
    }
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent?.isComposing) return;
    event.preventDefault();
    handleSend(event);
  };

  const handleReplyToMessage = (message) => {
    const replyInstanceId = String(message.instance_id || '');
    const instanceIsAvailable = availableInstances.some(
      (instance) => String(instance.id) === replyInstanceId,
    );
    if (!instanceIsAvailable) {
      setLastError('Você não tem permissão de envio no canal desta mensagem.');
      return;
    }
    setSelectedInstanceId(replyInstanceId);
    setReplyingTo(message);
    setShowEmojiPicker(false);
    window.setTimeout(() => messageInputRef.current?.focus(), 0);
  };

  const insertEmoji = (emoji) => {
    const input = messageInputRef.current;
    const start = input?.selectionStart ?? text.length;
    const end = input?.selectionEnd ?? text.length;
    const nextText = `${text.slice(0, start)}${emoji}${text.slice(end)}`;
    setText(nextText);
    setShowEmojiPicker(false);
    window.setTimeout(() => {
      input?.focus();
      input?.setSelectionRange(start + emoji.length, start + emoji.length);
    }, 0);
  };

  const handleSuggestAiResponse = async () => {
    if (!contactId || loadingAiSuggestion) return;

    setLoadingAiSuggestion(true);
    setLastError('');
    setMessageType('text');
    resetMediaDraft();

    try {
      const response = await suggestAiReply(selectedInstanceId);
      const suggestion = String(response?.suggestion || '').trim();
      if (suggestion) setText(suggestion);
    } catch (error) {
      setLastError(error.response?.data?.message || error.message || 'Não foi possível gerar uma sugestão de IA.');
    } finally {
      setLoadingAiSuggestion(false);
    }
  };

  const handleMediaFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;
    let nextType = 'document';
    if (file.type.startsWith('image/')) nextType = 'image';
    if (file.type.startsWith('audio/')) nextType = 'audio';
    if (file.type.startsWith('video/')) nextType = 'video';
    if (selectedInstanceIsMeta) {
      const rule = META_MEDIA_RULES[nextType];
      if (!rule.mime.has(String(file.type || '').toLowerCase())) {
        setLastError(`Formato de ${rule.label} não aceito pela Cloud API oficial.`);
        event.target.value = '';
        return;
      }
      if (file.size > rule.max) {
        setLastError(`O arquivo excede o limite oficial de ${Math.round(rule.max / 1024 / 1024)} MB para ${rule.label}.`);
        event.target.value = '';
        return;
      }
    }
    setSelectedMediaFile(file);
    setMediaUrl('');
    setFileName(file.name);
    setMessageType(nextType);
    event.target.value = '';
  };

  const openMediaPicker = (type) => {
    if (recordingAudio) return;
    resetMediaDraft();
    setMessageType('text');
    if (type === 'image') imageInputRef.current?.click();
    if (type === 'video') videoInputRef.current?.click();
    if (type === 'document') documentInputRef.current?.click();
  };

  const stopRecordingTracks = () => {
    recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
    recordingStreamRef.current = null;
  };

  const startAudioRecording = async () => {
    if (recordingAudio) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setLastError('Seu navegador não permite gravação de áudio nesta tela.');
      return;
    }

    try {
      setLastError('');
      resetMediaDraft();
      setMessageType('audio');
      setRecordingSeconds(0);
      cancelRecordingRef.current = false;
      audioChunksRef.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordingStreamRef.current = stream;
      const preferredMime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType: preferredMime });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data?.size) audioChunksRef.current.push(event.data);
      };

      recorder.onstop = async () => {
        const chunks = audioChunksRef.current;
        const cancelled = cancelRecordingRef.current;
        audioChunksRef.current = [];
        stopRecordingTracks();
        setRecordingAudio(false);

        if (cancelled || chunks.length === 0) return;
        try {
          const mimeType = recorder.mimeType || 'audio/webm';
          const recordedBlob = new Blob(chunks, { type: mimeType });
          const mp3Blob = await convertRecordedAudioToMp3(recordedBlob);
          const file = new File([mp3Blob], `audio-${Date.now()}.mp3`, { type: 'audio/mpeg' });
          setSelectedMediaFile(file);
          setFileName(file.name);
          setMediaUrl('');
          setCaption('');
          setMessageType('audio');
        } catch (error) {
          setMessageType('text');
          setLastError(error.message || 'Não foi possível preparar o áudio para envio.');
        }
      };

      recorder.start();
      setRecordingAudio(true);
    } catch (error) {
      stopRecordingTracks();
      setRecordingAudio(false);
      setLastError(error?.name === 'NotAllowedError'
        ? 'Permissão do microfone negada.'
        : 'Não foi possível iniciar a gravação de áudio.');
    }
  };

  const stopAudioRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      cancelRecordingRef.current = false;
      mediaRecorderRef.current.stop();
    }
  };

  const cancelAudioRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      cancelRecordingRef.current = true;
      mediaRecorderRef.current.stop();
      return;
    }
    resetMediaDraft();
    setMessageType('text');
  };

  const saveParticipant = async () => {
    if (contactId) {
      await updateContact(contactId, contactForm);
    }
    setSideModal('');
  };

  const hideSelectedConversation = async () => {
    if (!contactId || archivingContact) return;
    const contactLabel = selectedContact?.name
      || selectedContact?.phone_display
      || selectedContact?.phone_normalized
      || 'este contato';
    const confirmed = window.confirm(
      `Ocultar a conversa de ${contactLabel}?\n\nO histórico será preservado e a conversa voltará automaticamente se esse número enviar uma nova mensagem.`,
    );
    if (!confirmed) return;

    setArchivingContact(true);
    try {
      await archiveContact(contactId);
      setSideModal('');
      setMobilePane('conversations');
      setLastError('');
    } catch (error) {
      setLastError(error.response?.data?.message || error.message || 'Não foi possível ocultar a conversa.');
    } finally {
      setArchivingContact(false);
    }
  };

  const createCard = async () => {
    if (!contactId || !cardForm.column_id) return;
    await createCardFromContact(contactId, {
      column_id: cardForm.column_id,
      entity_id: cardForm.entity_id || user?.id,
      name: contactForm.name,
      email: contactForm.email,
      origem: 'WhatsApp',
    });
    setCardForm({ column_id: '', entity_id: '' });
    setSideModal('');
  };

  return (
    <div className="zapi-whatsapp-modal-backdrop" data-zapi-whatsapp data-theme={theme === 'dark' ? 'dark' : 'light'}>
      <div className="zapi-whatsapp-modal">
        <div className="zapi-whatsapp-modal-header">
          <div className="zapi-whatsapp-product-heading">
            <img src="/syncs-logo-meta-v1.png" alt="" aria-hidden="true" />
            <div>
              <strong>Syncs Messenger</strong>
              <span>Atendimento oficial via WhatsApp Business</span>
            </div>
          </div>
          <button type="button" className="zapi-whatsapp-icon-button" onClick={closeWhatsappZapi} title="Fechar">
            <FaTimes />
          </button>
        </div>

        {lastError && (
          <div className="zapi-whatsapp-alert" role="alert">
            <span>{lastError}</span>
            <div>
              {isLoadError && (
                <button type="button" onClick={() => void retryLoading()}>
                  <FaSyncAlt /> Tentar novamente
                </button>
              )}
              <button type="button" onClick={() => setLastError('')} aria-label="Fechar aviso">
                <FaTimes />
              </button>
            </div>
          </div>
        )}

        <div className={`zapi-whatsapp-modal-grid zapi-whatsapp-mobile-pane-${mobilePane}`}>
          {mobilePane === 'details' && (
            <button
              type="button"
              className="zapi-whatsapp-mobile-details-scrim"
              onClick={() => setMobilePane('chat')}
              aria-label="Fechar dados do participante"
            />
          )}
          <aside className="zapi-whatsapp-conversation-list">
            <div className="zapi-whatsapp-search-row">
              <FaSearch />
              <input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onKeyDown={(event) => event.key === 'Enter' && runSearch()}
                placeholder="Buscar participante ou telefone"
              />
              <button type="button" onClick={runSearch}>Buscar</button>
            </div>
            <div className="zapi-whatsapp-filter-row">
              <button type="button" className={filter === '' ? 'zapi-whatsapp-filter-active' : ''} onClick={() => { setFilter(''); loadConversations({ term: searchTerm }); }}>Todas</button>
              <button type="button" className={filter === 'unread' ? 'zapi-whatsapp-filter-active' : ''} onClick={() => { setFilter('unread'); loadConversations({ term: searchTerm, filter: 'unread' }); }}>Não lidas</button>
              <button type="button" className={filter === 'no_card' ? 'zapi-whatsapp-filter-active' : ''} onClick={() => { setFilter('no_card'); loadConversations({ term: searchTerm, filter: 'no_card' }); }}>Sem card</button>
            </div>
            <div className="zapi-whatsapp-conversation-scroll">
              {loadingConversations && <div className="zapi-whatsapp-empty">Carregando conversas...</div>}
              {!loadingConversations && conversations.length === 0 && (
                <div className="zapi-whatsapp-empty">Nenhuma conversa encontrada.</div>
              )}
              {conversations.map((conversation) => (
                <button
                  type="button"
                  key={getContactId(conversation)}
                  className={`zapi-whatsapp-conversation-item ${String(contactId) === String(getContactId(conversation)) ? 'zapi-whatsapp-conversation-item--active' : ''}`}
                  onClick={() => {
                    setMobilePane('chat');
                    void selectConversation(conversation);
                  }}
                >
                  <span className="zapi-whatsapp-conversation-name">
                    {conversation.name || conversation.phone_display || conversation.phone_normalized}
                  </span>
                  <span className="zapi-whatsapp-conversation-preview">
                    {conversation.last_message_direction === 'outbound' && (
                      <em className={`zapi-whatsapp-conversation-sender ${conversation.last_message_sent_by_me ? 'zapi-whatsapp-conversation-sender--me' : ''}`}>
                        {conversation.last_message_sent_by_me ? 'Você: ' : 'Equipe: '}
                      </em>
                    )}
                    {formatConversationPreview(conversation.last_message_preview)}
                  </span>
                  <span className="zapi-whatsapp-conversation-meta">
                    {conversation.last_message_at ? formatDateTime(conversation.last_message_at) : ''}
                    {conversation.unread_count > 0 && <b>{conversation.unread_count}</b>}
                  </span>
                </button>
              ))}
            </div>
          </aside>

          <main className="zapi-whatsapp-chat-panel">
            {!selectedContact && (
              <div className="zapi-whatsapp-chat-empty">Selecione uma conversa para iniciar.</div>
            )}
            {selectedContact && (
              <>
                <div className="zapi-whatsapp-chat-header">
                  <div className="zapi-whatsapp-chat-heading">
                    <button
                      type="button"
                      className="zapi-whatsapp-mobile-navigation-button"
                      onClick={() => setMobilePane('conversations')}
                      aria-label="Voltar para conversas"
                      title="Voltar para conversas"
                    >
                      <FaArrowLeft aria-hidden="true" />
                    </button>
                    <div className="zapi-whatsapp-chat-identity">
                      <strong>{selectedContact.name || selectedContact.phone_display || selectedContact.phone_normalized}</strong>
                      <span>{selectedContact.instance_labels || 'Linha do tempo unificada por participante'}</span>
                    </div>
                  </div>
                  <div className="zapi-whatsapp-chat-header-actions">
                    <span className="zapi-whatsapp-chat-contact-phone">{selectedContact.phone_display || selectedContact.phone_normalized}</span>
                    {(selectedContact.provider === 'meta' || selectedContact.providers?.includes('meta')) && (
                      <button
                        type="button"
                        className="zapi-whatsapp-mark-unread-button"
                        onClick={handleMarkUnread}
                        disabled={markingUnread}
                        aria-label="Marcar como não lida"
                        title="Marcar como não lida (só para você, o cliente não é avisado)"
                      >
                        <FaEnvelope aria-hidden="true" />
                      </button>
                    )}
                    <button
                      type="button"
                      className="zapi-whatsapp-mobile-navigation-button"
                      onClick={() => setMobilePane('details')}
                      aria-label="Abrir dados e ações do participante"
                      title="Dados e ações do participante"
                    >
                      <FaAddressBook aria-hidden="true" />
                    </button>
                  </div>
                </div>

                <div
                  ref={messagesContainerRef}
                  className="zapi-whatsapp-messages"
                  onPointerDown={markSelectedConversationRead}
                  onScroll={handleConversationScroll}
                  onTouchMove={markSelectedConversationRead}
                >
                  {loadingMessages && <div className="zapi-whatsapp-empty">Carregando mensagens...</div>}
                  {messages.map((message) => {
                    const identity = getMessageIdentityPresentation(message, selectedContact);
                    const hasStorageStatus = message.provider === 'meta'
                      && Boolean(message.media_id)
                      && !['text', 'template'].includes(String(message.message_type || '').toLowerCase());

                    return (
                      <div
                        key={`${message.provider || 'zapi'}:${message.id}`}
                        className={`zapi-whatsapp-message-row zapi-whatsapp-message-row--${message.direction}`}
                      >
                        <div className={`zapi-whatsapp-message-bubble${hasStorageStatus ? ' zapi-whatsapp-message-bubble--storage' : ''}`}>
                          <MessageActions
                            message={message}
                            onReply={handleReplyToMessage}
                            onError={setLastError}
                            empresaId={user?.empresa_id}
                            userId={user?.id || user?.user_id}
                            theme={theme}
                          />
                          <div className="zapi-whatsapp-message-meta">
                            <strong>{identity.primary}</strong>
                            {identity.secondary && <span>{identity.secondary}</span>}
                          </div>
                          <MessageReplyReference message={message} contact={selectedContact} />
                          {message.text_body && <div className="zapi-whatsapp-message-text">{message.text_body}</div>}
                          <MessageMedia
                            message={message}
                            empresaId={user?.empresa_id}
                            userId={user?.id || user?.user_id}
                          />
                          {message.caption && <div className="zapi-whatsapp-message-caption">{message.caption}</div>}
                          <div className="zapi-whatsapp-message-status">
                            {identity.isOutbound && <span>{identity.sender}</span>}
                            <time dateTime={message.occurred_at || undefined}>{formatDateTime(message.occurred_at)}</time>
                            <MessageDeliveryStatus message={message} />
                            {message.error_message && <span className="zapi-whatsapp-message-error">{message.error_message}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {availableInstances.length === 0 ? (
                  <div className="zapi-whatsapp-composer zapi-whatsapp-composer-locked">
                    <strong>Conversa somente para consulta</strong>
                    <span>Você pode ver esta conversa porque tem acesso ao card vinculado. Para responder, entre com um usuário autorizado em um número ativo.</span>
                  </div>
                ) : (
                  <form className="zapi-whatsapp-composer" onSubmit={handleSend}>
                    <input ref={imageInputRef} type="file" accept={selectedInstanceIsMeta ? META_IMAGE_ACCEPT : 'image/*'} onChange={handleMediaFileUpload} className="zapi-whatsapp-hidden-file-input" disabled={sending || metaFreeFormBlocked} />
                    <input ref={videoInputRef} type="file" accept={selectedInstanceIsMeta ? META_VIDEO_ACCEPT : 'video/*'} onChange={handleMediaFileUpload} className="zapi-whatsapp-hidden-file-input" disabled={sending || metaFreeFormBlocked} />
                    <input ref={documentInputRef} type="file" accept={selectedInstanceIsMeta ? META_DOCUMENT_ACCEPT : undefined} onChange={handleMediaFileUpload} className="zapi-whatsapp-hidden-file-input" disabled={sending || metaFreeFormBlocked} />
                    <div className="zapi-whatsapp-composer-toolbar">
                      <select value={selectedInstanceId} onChange={(event) => {
                        channelChoiceRef.current.manual = true;
                        setSelectedInstanceId(event.target.value);
                      }}>
                        {!selectedInstanceId && <option value="">Selecione o número que fará o envio</option>}
                        {availableInstances.map((instance) => (
                          <option key={instance.id} value={instance.id}>
                            {instance.name} {instance.phone_display ? `- ${instance.phone_display}` : ''}
                          </option>
                        ))}
                      </select>
                      <div className="zapi-whatsapp-composer-tools">
                        <button type="button" disabled={metaFreeFormBlocked} className={messageType === 'text' ? 'zapi-whatsapp-tool-active' : ''} onClick={() => { setMessageType('text'); resetMediaDraft(); }} title={metaFreeFormBlocked ? 'Janela de 24 horas fechada' : 'Texto'}><FaKeyboard /></button>
                        <button type="button" disabled={metaFreeFormBlocked} className={messageType === 'image' ? 'zapi-whatsapp-tool-active' : ''} onClick={() => openMediaPicker('image')} title={metaFreeFormBlocked ? 'Janela de 24 horas fechada' : 'Selecionar imagem'}><FaImage /></button>
                        <button type="button" disabled={metaFreeFormBlocked} className={recordingAudio || messageType === 'audio' ? 'zapi-whatsapp-tool-active' : ''} onClick={recordingAudio ? stopAudioRecording : startAudioRecording} title={metaFreeFormBlocked ? 'Janela de 24 horas fechada' : recordingAudio ? 'Parar gravação' : 'Gravar áudio'}><FaMicrophone /></button>
                        <button type="button" disabled={metaFreeFormBlocked} className={messageType === 'video' ? 'zapi-whatsapp-tool-active' : ''} onClick={() => openMediaPicker('video')} title={metaFreeFormBlocked ? 'Janela de 24 horas fechada' : 'Selecionar vídeo'}><FaVideo /></button>
                        <button type="button" disabled={metaFreeFormBlocked} className={messageType === 'document' ? 'zapi-whatsapp-tool-active' : ''} onClick={() => openMediaPicker('document')} title={metaFreeFormBlocked ? 'Janela de 24 horas fechada' : 'Selecionar arquivo'}><FaFileAlt /></button>
                        {selectedInstanceIsMeta && (
                          <button type="button" className={messageType === 'template' ? 'zapi-whatsapp-tool-active' : ''} onClick={() => { setMessageType('template'); resetMediaDraft(); setReplyingTo(null); }} title="Selecionar modelo aprovado"><FaCheckCircle /></button>
                        )}
                        <button
                          type="button"
                          className="zapi-whatsapp-ai-suggestion-button"
                          onClick={handleSuggestAiResponse}
                          disabled={!contactId || loadingAiSuggestion || metaFreeFormBlocked}
                          title={loadingAiSuggestion ? 'Gerando sugestão...' : 'Gerar rascunho com IA; nada será enviado automaticamente'}
                          aria-label="Gerar rascunho de resposta com IA"
                        >
                          <FaRobot />
                          <span>{loadingAiSuggestion ? 'Gerando…' : 'Rascunho IA'}</span>
                        </button>
                      </div>
                    </div>
                    {selectedInstance && selectedInstance.connected === false && (
                      <div className="zapi-whatsapp-warning">Esta instância aparece como desconectada. O envio pode falhar.</div>
                    )}
                    {selectedInstanceIsMeta && (
                      <>
                        <div className={`zapi-whatsapp-policy-notice ${metaServiceWindowOpen ? 'zapi-whatsapp-policy-notice--open' : 'zapi-whatsapp-policy-notice--closed'}`}>
                          {metaServiceWindowOpen ? (
                            <>
                              <strong>Janela de atendimento aberta</strong>
                              <span>Texto e mídia podem ser enviados por aproximadamente {serviceWindowRemaining}.</span>
                            </>
                          ) : (
                            <>
                              <strong>Janela de 24 horas fechada</strong>
                              <span>Texto e mídia foram bloqueados. Use somente um modelo aprovado e registre como o cliente autorizou o contato.</span>
                              <button type="button" onClick={() => { setMessageType('template'); resetMediaDraft(); setReplyingTo(null); }}>Selecionar modelo aprovado</button>
                            </>
                          )}
                        </div>
                      </>
                    )}
                    {recordingAudio && (
                      <div className="zapi-whatsapp-recording-panel">
                        <span className="zapi-whatsapp-recording-dot" />
                        <strong>Gravando áudio {formatRecordingDuration(recordingSeconds)}</strong>
                        <button type="button" onClick={stopAudioRecording}><FaStop /> Usar áudio</button>
                        <button type="button" onClick={cancelAudioRecording}><FaTrash /> Cancelar</button>
                      </div>
                    )}
                    {selectedMediaFile && (
                      <div className="zapi-whatsapp-selected-file">
                        <span>{messageType === 'audio' ? 'Áudio pronto para enviar' : selectedMediaFile.name}</span>
                        <button type="button" onClick={cancelAudioRecording} title="Remover arquivo">
                          <FaTrash />
                        </button>
                      </div>
                    )}
                    {replyingTo && (
                      <div className="zapi-whatsapp-replying-banner">
                        <FaReply />
                        <div>
                          <strong>Respondendo a {replyingTo.direction === 'outbound' ? 'Syncs CRM' : selectedContact.name || 'cliente'}</strong>
                          <span>{messageContentPreview(replyingTo)}</span>
                        </div>
                        <button type="button" onClick={() => setReplyingTo(null)} title="Cancelar resposta" aria-label="Cancelar resposta">
                          <FaTimes />
                        </button>
                      </div>
                    )}
                    {messageType === 'template' && (
                      <div className="zapi-whatsapp-template-panel">
                        <select value={selectedTemplateId} onChange={(event) => setSelectedTemplateId(event.target.value)}>
                          <option value="">Selecione um modelo aprovado</option>
                          {metaTemplates.map((template) => (
                            <option key={template.id} value={template.id}>
                              {template.name} ({template.language}){template.category === 'MARKETING' && !metaMarketingEnabled ? ' — Marketing desligado' : ''}
                            </option>
                          ))}
                        </select>
                        {selectedTemplate && (
                          <p>Categoria informada pela Meta: <strong>{selectedTemplateCategoryLabel}</strong>. Use somente para a finalidade aprovada, com autorização do destinatário. A Meta pode cobrar o envio e alterar o status do modelo.</p>
                        )}
                        {selectedTemplateMarketingBlocked && (
                          <div className="zapi-whatsapp-template-status zapi-whatsapp-template-status--blocked" role="alert">
                            <strong>Modelos de Marketing estão desligados</strong>
                            <span>O administrador ainda não habilitou o envio de Marketing pela API oficial. Escolha um modelo de Utilidade.</span>
                          </div>
                        )}
                        {unsupportedTemplateCount > 0 && (
                          <p>{unsupportedTemplateCount} modelo(s) aprovado(s) não aparecem aqui porque exigem componentes que esta tela ainda não preenche com segurança.</p>
                        )}
                        {selectedTemplate && templateValues.map((value, index) => (
                          <input key={index} value={value} onChange={(event) => setTemplateValues((current) => current.map((item, position) => position === index ? event.target.value : item))} placeholder={`Valor {{${index + 1}}}`} />
                        ))}
                        {selectedTemplate && !selectedTemplateMarketingBlocked && (
                          <div
                            className={`zapi-whatsapp-template-status zapi-whatsapp-template-status--${selectedTemplateConsentGranted ? 'ok' : optInStatus === null ? 'pending' : 'blocked'}`}
                            role="status"
                          >
                            {selectedTemplateConsentGranted ? (
                              <>
                                <strong>Passo 1 concluído: consentimento de {selectedTemplateCategoryLabel} registrado</strong>
                                <span>{templateValues.some((value) => !value.trim()) ? 'Preencha os campos do modelo e envie.' : 'Pronto para enviar.'}</span>
                              </>
                            ) : optInStatus === null ? (
                              <strong>Verificando consentimento do cliente...</strong>
                            ) : optInStatus === 'error' ? (
                              <>
                                <strong>Não foi possível verificar o consentimento</strong>
                                <button type="button" onClick={refreshOptInStatus}>Tentar novamente</button>
                              </>
                            ) : (
                              <>
                                <strong>Passo 1 pendente: registrar consentimento de {selectedTemplateCategoryLabel}</strong>
                                <span>O envio fica bloqueado até você preencher Origem e Evidência abaixo e clicar em “Registrar consentimento”.</span>
                              </>
                            )}
                          </div>
                        )}
                        {selectedTemplate && !selectedTemplateMarketingBlocked && (
                          <div className="zapi-whatsapp-template-optin">
                            <p>Registre a origem e a evidência verdadeira da autorização. O nome do atendente, sozinho, não comprova consentimento.</p>
                            <input value={optInSource} onChange={(event) => setOptInSource(event.target.value)} placeholder="Origem: formulário, contrato, conversa presencial..." />
                            <input value={optInEvidence} onChange={(event) => setOptInEvidence(event.target.value)} placeholder="Evidência: data, referência, trecho ou localização do comprovante" />
                            <button type="button" onClick={() => recordTemplateConsent('granted')} disabled={!optInSource.trim() || !optInEvidence.trim()}>Registrar consentimento</button>
                            <button type="button" onClick={() => recordTemplateConsent('revoked')} disabled={!optInSource.trim() || !optInEvidence.trim()}>Revogar esta finalidade</button>
                            <button type="button" onClick={() => recordTemplateConsent('revoked', 'all')} disabled={!optInSource.trim() || !optInEvidence.trim()}>Registrar interrupção geral</button>
                          </div>
                        )}
                      </div>
                    )}
                    {showSlashSuggestions && (
                      <div className="zapi-whatsapp-slash-suggestions" role="listbox" aria-label="Mensagens rápidas">
                        {slashSuggestions.map((item, index) => (
                          <button
                            type="button"
                            key={item.id}
                            role="option"
                            aria-selected={index === slashIndex}
                            className={index === slashIndex ? 'is-active' : ''}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => applyQuickReply(item)}
                          >
                            <strong>/{item.atalho}</strong>
                            <span>{fillQuickReply(item.texto, selectedContact)}</span>
                          </button>
                        ))}
                        <small>↑ ↓ para escolher · Enter ou Tab para inserir · Esc para fechar</small>
                      </div>
                    )}
                    <div className="zapi-whatsapp-composer-row">
                      <QuickRepliesPanel
                        replies={quickReplies}
                        loadError={quickRepliesError}
                        onInsert={applyQuickReply}
                        onReload={loadQuickReplies}
                        canManageCompany={Number(user?.access_level) === 8}
                        disabled={!quickRepliesEnabled}
                      />
                      <StickersPanel
                        onSend={handleSendSticker}
                        canManageCompany={Number(user?.access_level) === 8}
                        disabled={!quickRepliesEnabled || !selectedInstanceIsMeta || sending}
                      />
                      <div className="zapi-whatsapp-emoji-picker" ref={emojiPickerRef}>
                        <button
                          type="button"
                          className="zapi-whatsapp-emoji-button"
                          onClick={() => setShowEmojiPicker((current) => !current)}
                          disabled={messageType === 'template' || metaFreeFormBlocked}
                          title="Inserir emoji"
                          aria-label="Inserir emoji"
                          aria-expanded={showEmojiPicker}
                        >
                          <FaSmile />
                        </button>
                        {showEmojiPicker && (
                          <div className="zapi-whatsapp-emoji-panel" role="menu" aria-label="Emojis">
                            {QUICK_EMOJIS.map((emoji) => (
                              <button key={emoji} type="button" onClick={() => insertEmoji(emoji)} aria-label={`Inserir ${emoji}`}>
                                {emoji}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <textarea
                        ref={messageInputRef}
                        value={text}
                        onChange={(event) => setText(event.target.value)}
                        onKeyDown={handleMessageKeyDown}
                        onFocus={markSelectedConversationRead}
                        onClick={markSelectedConversationRead}
                        placeholder={messageType === 'text' ? 'Digite a mensagem' : messageType === 'template' ? (templateBlockReason || 'Modelo pronto. Clique em enviar.') : 'Adicionar uma legenda'}
                        disabled={messageType === 'template' || metaFreeFormBlocked}
                      />
                      <button
                        type="submit"
                        title={templateBlockReason || (metaFreeFormBlocked && messageType !== 'template' ? 'Selecione um modelo aprovado' : 'Enviar mensagem')}
                        disabled={
                          sending
                          || !selectedInstanceId
                          || !selectedInstance
                          || (metaFreeFormBlocked && messageType !== 'template')
                          || Boolean(templateBlockReason)
                        }
                      >
                        <FaPaperPlane />
                      </button>
                    </div>
                  </form>
                )}
              </>
            )}
          </main>

          <aside className="zapi-whatsapp-side-panel">
            <div className="zapi-whatsapp-mobile-side-header">
              <button
                type="button"
                className="zapi-whatsapp-mobile-navigation-button"
                onClick={() => setMobilePane('chat')}
                aria-label="Voltar para a conversa"
                title="Voltar para a conversa"
              >
                <FaArrowLeft aria-hidden="true" />
              </button>
              <strong>Dados e ações</strong>
            </div>
            <section className="zapi-whatsapp-panel-section zapi-whatsapp-side-actions">
              <h3>Ações do participante</h3>
              {!selectedContact && <p>Selecione uma conversa.</p>}
              {selectedContact && (
                <div className="zapi-whatsapp-side-action-grid">
                  <button type="button" onClick={() => setSideModal('contact')}>
                    <FaAddressBook /> Editar participante
                  </button>
                  <button type="button" onClick={() => setSideModal('create-card')}>
                    <FaPlus /> Criar card
                  </button>
                  {selectedInstanceIsMeta && (
                    <button type="button" onClick={() => setSideModal('contact-preferences')}>
                      <FaBan /> Preferências de contato
                    </button>
                  )}
                  {!loadingLinkedCardPreviews && linkedCards.length === 0 && (
                    <button type="button" onClick={hideSelectedConversation} disabled={archivingContact}>
                      <FaEyeSlash /> {archivingContact ? 'Ocultando...' : 'Ocultar conversa'}
                    </button>
                  )}
                </div>
              )}
            </section>

            <section className="zapi-whatsapp-panel-section">
              <h3>Cards vinculados</h3>
              {loadingLinkedCardPreviews && <p>Carregando cards vinculados...</p>}
              {!loadingLinkedCardPreviews && linkedCards.length === 0 && <p>Nenhum card vinculado.</p>}
              {!loadingLinkedCardPreviews && linkedCardPreviews.length > 0 && (
                <div className="zapi-whatsapp-linked-card-preview-list">
                  {linkedCardPreviews.map((card) => (
                    <div key={card.card_id} className="zapi-whatsapp-linked-card-preview">
                      <PreviewCard cardData={card} />
                    </div>
                  ))}
                </div>
              )}
            </section>
          </aside>
        </div>

        {sideModal && selectedContact && (
          <div className="zapi-whatsapp-nested-modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && setSideModal('')}>
            <div className="zapi-whatsapp-nested-modal">
              <div className="zapi-whatsapp-nested-modal-header">
                <h3>
                  {sideModal === 'contact' && 'Editar participante'}
                  {sideModal === 'create-card' && 'Criar card'}
                  {sideModal === 'contact-preferences' && 'Preferências de contato'}
                </h3>
                <button type="button" className="zapi-whatsapp-icon-button" onClick={() => setSideModal('')} title="Fechar">
                  <FaTimes />
                </button>
              </div>

              {sideModal === 'contact' && (
                <div className="zapi-whatsapp-nested-modal-body">
                  <input value={contactForm.name || ''} onChange={(event) => setContactForm({ ...contactForm, name: event.target.value })} placeholder="Nome" />
                  <input value={contactForm.phone || ''} onChange={(event) => setContactForm({ ...contactForm, phone: event.target.value })} placeholder="Telefone" />
                  <input value={contactForm.email || ''} onChange={(event) => setContactForm({ ...contactForm, email: event.target.value })} placeholder="Email" />
                  <div className="zapi-whatsapp-two-cols">
                    <input value={contactForm.state || ''} onChange={(event) => setContactForm({ ...contactForm, state: event.target.value })} placeholder="UF" />
                    <input value={contactForm.city || ''} onChange={(event) => setContactForm({ ...contactForm, city: event.target.value })} placeholder="Cidade" />
                  </div>
                  <textarea value={contactForm.notes || ''} onChange={(event) => setContactForm({ ...contactForm, notes: event.target.value })} placeholder="Observações" />
                  <button type="button" className="zapi-whatsapp-primary-action" onClick={saveParticipant}>
                    Salvar participante
                  </button>
                </div>
              )}

              {sideModal === 'create-card' && (
                <div className="zapi-whatsapp-nested-modal-body">
                  <select value={cardForm.column_id} onChange={(event) => setCardForm({ ...cardForm, column_id: event.target.value })}>
                    <option value="">Selecione a coluna</option>
                    {columns.map((column) => (
                      <option key={column.id} value={column.id}>{column.name}</option>
                    ))}
                  </select>
                  <select value={cardForm.entity_id || ''} onChange={(event) => setCardForm({ ...cardForm, entity_id: event.target.value })}>
                    <option value="">Responsável atual</option>
                    {listAllUsers.map((item) => (
                      <option key={item.id} value={item.id}>{item.username}</option>
                    ))}
                  </select>
                  <button type="button" className="zapi-whatsapp-primary-action" onClick={createCard} disabled={!selectedContact || !cardForm.column_id}>
                    <FaPlus /> Criar novo card
                  </button>
                </div>
              )}

              {sideModal === 'contact-preferences' && (
                <div className="zapi-whatsapp-nested-modal-body zapi-whatsapp-contact-preferences">
                  <div className="zapi-whatsapp-contact-preferences-notice">
                    <FaExclamationTriangle />
                    <p>
                      Use esta opção somente quando o cliente pedir que as mensagens sejam interrompidas
                      por telefone, e-mail, atendimento presencial ou outro canal fora desta conversa.
                    </p>
                  </div>
                  <label htmlFor="zapi-optout-source">Onde o pedido foi recebido</label>
                  <input
                    id="zapi-optout-source"
                    value={optInSource}
                    onChange={(event) => setOptInSource(event.target.value)}
                    placeholder="Ex.: telefone, e-mail ou atendimento presencial"
                  />
                  <label htmlFor="zapi-optout-evidence">Comprovação do pedido</label>
                  <input
                    id="zapi-optout-evidence"
                    value={optInEvidence}
                    onChange={(event) => setOptInEvidence(event.target.value)}
                    placeholder="Ex.: data, referência ou localização do comprovante"
                  />
                  <button
                    type="button"
                    className="zapi-whatsapp-danger-action"
                    onClick={() => recordTemplateConsent('revoked', 'all')}
                    disabled={!optInSource.trim() || !optInEvidence.trim()}
                  >
                    <FaBan /> Registrar interrupção de mensagens
                  </button>
                </div>
              )}

            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default WhatsappZapiModal;
